import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { OperationsIntakeItemsDto, OperationsItemDto, OperationsItemsFacetsDto, OperationsItemsSortKey } from "@/api/endpoints";
import { BrandedLoader } from "@/components/BrandedLoader";
import { TableSkeleton } from "@/components/Skeleton";
import { ATTENTION_CLASS, ATTENTION_LABEL, STAGE_LABEL, waitingOnLabel, workTypeLabel } from "../operationsLabels";
import { fmtETDate } from "@/lib/format";
import { StartDateCell } from "./StartRiskCells";
import { countLabel, effectiveSort, hasViewFilters, nextSort, sortSummary, viewFilterSummary, type DrawerView } from "../drawerView";
import { DrawerFilterBar } from "./DrawerFilterBar";
import { intakeHref } from "./IntakeItemsTable";
import { INTAKE_SOURCE_LABEL } from "../operationsLabels";
import { deskRows, mergeFacets, type DeskRow } from "../deskRows";

interface Props {
  title: string;
  meta: string;
  items: OperationsItemDto[];
  totalCount: number;
  /** Items in the bucket before the drawer's filters; shown as "180 of 370". */
  bucketCount: number;
  cap: number;
  loading: boolean;
  /** The rows shown belong to the previous filters while the new ones load (placeholder data): dim them and say so. */
  updating?: boolean;
  /** The API answered 503 while its caches warm; the query is retrying on its own. */
  warming?: boolean;
  error: string | null;
  /** Pending for (bd) warms past this (the Blocked clock's threshold). */
  blockedAfterBd: number;
  hideOwner?: boolean;
  /** The bucket is a period cohort (an owner's created / completed requests, 2026-10-28), not open work: the note says so. */
  cohort?: "started" | "ended";
  /** The bucket is one team / one division (the owner table's group rows, 2026-10-26): that dropdown is hidden. */
  hideTeam?: boolean;
  hideDivision?: boolean;
  /** The bucket is one manager's teams (the owner table's manager row, 2026-10-27): the Manager dropdown is hidden. */
  hideManager?: boolean;
  /** Sort, facet selections and search; the API applies them to the whole bucket. */
  view: DrawerView;
  facets: OperationsItemsFacetsDto | undefined;
  onViewChange: (next: Partial<DrawerView>) => void;
  /**
   * The owner, team and division desk views (2026-10-27): the intakes the person holds while a journey waits at intake
   * (nothing in FBO yet, or the work order waiting for its request), listed in the same table as the requests, sorted
   * with them; the chips and the search apply to both. Absent on other drills.
   */
  intakes?: IntakesHeld;
  onClose: () => void;
  onRetry: () => void;
}

export interface IntakesHeld {
  data: OperationsIntakeItemsDto | undefined;
  loading: boolean;
  updating: boolean;
  error: string | null;
  onRetry: () => void;
}

interface Column {
  label: string;
  sort?: OperationsItemsSortKey;
  className?: string;
  /** Hover text on the header: which clock the column counts. */
  tip?: string;
}

/** Role pill + actor name for a task, "Work order · to approve" for a waiting work order, else "no pending action". */
function PendingWithCell({ waitingOn }: { waitingOn: OperationsItemDto["waitingOn"] }) {
  if (!waitingOn) {
    return <div className="ops-pending"><span className="ops-pill role-none">no pending action</span></div>;
  }
  if (waitingOn.kind === "workOrder") {
    return (
      <div className="ops-pending">
        <span className="ops-pill role-workorder" title="No task is pending; the work order is waiting on our side.">{waitingOn.detail ? `Work order · ${waitingOn.detail}` : "Work order"}</span>
      </div>
    );
  }
  const role = waitingOn.role ?? "Other";
  const roleLabel = waitingOnLabel({ ...waitingOn, detail: null });
  return (
    <div className="ops-pending">
      <span className={`ops-pill role-${role.toLowerCase()}`} title="Who holds the first open task">{roleLabel}</span>
      {waitingOn.detail && <span className="muted">{waitingOn.detail}</span>}
    </div>
  );
}

/** Drill-through / desk view: the requests behind a number. Rows open Request detail in a new tab. */
export function ItemsDrawer(props: Props) {
  const { title, meta, items, totalCount, bucketCount, cap, loading, updating, warming, error, blockedAfterBd, hideOwner, hideTeam, hideDivision, hideManager, cohort, view, facets, onViewChange, intakes, onClose } = props;
  const filtered = hasViewFilters(view);
  const active = effectiveSort(view);
  const columns: Column[] = [
    { label: "Worker", sort: "worker" },
    { label: "Client", sort: "client" },
    { label: "Type", sort: "type" },
    { label: "Stage", sort: "stage", className: "fit" },
    { label: "Age (bd)", sort: "age", className: "numeric", tip: "Whole business days since the request was created." },
    { label: "Started", className: "fit", tip: "The day the journey started: the intake when there is one, else the request." },
    { label: "Pending for (bd)", sort: "pendingFor", className: "numeric", tip: `Whole business days the first open task has sat with its actor, or the work order has waited. Over ${blockedAfterBd} = Blocked.` },
    { label: "Start", sort: "startLead", className: "fit", tip: "The work-order version's contract start date with the lead in business days (negative = already started); the pill marks it At risk or Breached. Onboarding and extension requests with an unapproved work order." },
    { label: "Attention", sort: "attention", className: "fit" },
    { label: "Pending with", sort: "pendingWith" },
    ...(hideOwner ? [] : [{ label: "Owner", sort: "owner" as const }]),
  ];
  const clearView = () => onViewChange({ attention: [], pendingWith: [], workType: [], startRisk: [], owner: [], team: [], division: [], manager: [], engagement: [], holder: [], q: "" });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Says "scroll sideways" in the footer when the table is wider than the drawer body, so a
  // column cut at the right edge reads as "more to the right", not as a bug.
  const bodyRef = useRef<HTMLDivElement>(null);
  const [overflowsX, setOverflowsX] = useState(false);
  useLayoutEffect(() => {
    const body = bodyRef.current;
    if (!body) return;
    const measure = () => setOverflowsX(body.scrollWidth > body.clientWidth + 1);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(body);
    return () => observer.disconnect();
  }, [items.length, loading, error]);

  // `via` tells the detail page where "back" is: a new tab has no history to step back through.
  const detailHref = (id: string) => `/requests/${encodeURIComponent(id)}?via=operations`;
  const open = (id: string) => window.open(detailHref(id), "_blank", "noopener");
  const cols = hideOwner ? 10 : 11;
  // One list (Rafael, 2026-10-27): the owner's intakes merged into the request rows by the active sort, the chips applied to them locally.
  const rows = useMemo<DeskRow[]>(() => deskRows(items, intakes?.data?.items ?? [], view), [items, intakes?.data?.items, view]);
  const heldShown = rows.length - items.length;
  const heldTotal = intakes?.data?.totalCount ?? 0;
  const shownFacets = useMemo(() => mergeFacets(facets, intakes?.data?.items ?? []), [facets, intakes?.data?.items]);


  /** A request row (the original list); `i` is the item. */
  function RequestRow({ i }: { i: OperationsItemDto }) {
    return (
                    <tr className="linkrow" onClick={() => open(i.request.id)}>
                      <td className="worker">
                        <a className="row-link" href={detailHref(i.request.id)} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} aria-label={`Open request ${i.request.id.slice(0, 8)} in a new tab`}>
                          <b>{i.request.workerName}</b>
                          <span className="material-symbols-rounded" aria-hidden="true">open_in_new</span>
                        </a>
                      </td>
                      <td className="client">{i.request.client}</td>
                      <td className="type">{workTypeLabel(i.workType)}</td>
                      <td className="fit">{i.stage ? <span className="ops-pill stage">{STAGE_LABEL[i.stage]}</span> : <span className="muted">—</span>}</td>
                      <td className="numeric">{i.ageBd}</td>
                      <td className="fit nowrap">{fmtETDate(i.request.requestCreatedDateTime)}</td>
                      <td className={`numeric${i.pendingForBd != null && i.pendingForBd > blockedAfterBd ? " warm" : ""}`}>
                        {i.pendingForBd ?? <span className="muted">—</span>}
                      </td>
                      <StartDateCell risk={i.startRisk} leadBd={i.startLeadBd} startDate={i.startDate} />
                      <td className="fit">
                        {i.attention.length > 0 ? (
                          <span className="ops-pills">
                            {i.attention.map((a) => (
                              <span key={a} className={`ops-pill att-${ATTENTION_CLASS[a]}`}><i className="dot" />{ATTENTION_LABEL[a]}</span>
                            ))}
                          </span>
                        ) : <span className="muted">—</span>}
                      </td>
                      <td>
                        <PendingWithCell waitingOn={i.waitingOn} />
                      </td>
                      {!hideOwner && <td className="nowrap">{i.owner}</td>}
                    </tr>
    );
  }

  return (
    <>
      <div className="ops-drawer-backdrop" onClick={onClose} aria-hidden="true" />
      <aside className="ops-drawer" role="dialog" aria-modal="true" aria-labelledby="ops-drawer-title">
        <div className="ops-drawer-head">
          <div>
            <h2 id="ops-drawer-title">{title}</h2>
            {/* A desk view (requests + the intakes the owner holds) counts the merged list, the same number as the status line and the footer (2026-10-27). */}
            <div className="meta">{intakes ? `${countLabel(totalCount + heldShown, bucketCount + heldTotal)} · ${meta}${heldTotal > 0 ? ` · ${heldTotal} intake${heldTotal === 1 ? "" : "s"} held` : ""}` : meta}</div>
          </div>
          <button type="button" className="icon-btn" aria-label="Close" onClick={onClose}>
            <span className="material-symbols-rounded" aria-hidden="true">close</span>
          </button>
        </div>
        <div className="ops-drawer-note">
          Each row is a request{cohort ? ` ${cohort === "started" ? "created" : "completed"} in the period` : ""}; click it to open the Journey detail screen in a new tab. &ldquo;Pending with&rdquo; is who holds
          the first open task action in the FBO sequence (later tasks are queued behind it), or the work order when no task is
          pending; &ldquo;Pending for&rdquo; counts whole business days it has sat there: that is the Blocked clock (over {blockedAfterBd} = Blocked).
          Stuck is the other clock: nobody touched the request for more than a business day.
        </div>
        <DrawerFilterBar view={view} facets={shownFacets} hideOwner={hideOwner} hideTeam={hideTeam} hideDivision={hideDivision} hideManager={hideManager} onChange={onViewChange} />
        {filtered && !error && (
          <div className="ops-drawer-status" role="status">
            Showing <b>{totalCount + heldShown}</b>{totalCount + heldShown < bucketCount + heldTotal ? ` of ${bucketCount + heldTotal}` : ""} · {viewFilterSummary(view)}
            <button type="button" className="btn btn-text btn-sm" onClick={clearView}>Clear filters</button>
          </div>
        )}
        {updating && (
          <div className="ops-drawer-status ops-drawer-updating" role="status">
            <BrandedLoader variant="inline" label="Updating…" />
          </div>
        )}
        <div className={`ops-drawer-body${updating ? " is-stale" : ""}`} aria-busy={updating || undefined} ref={bodyRef}>
          {loading && items.length === 0 ? (
            <>
              {warming && (
                <p className="ops-drawer-warming" role="status">Snapshot warming on the server; retrying every 10 seconds.</p>
              )}
              <TableSkeleton cols={cols} rows={6} />
            </>
          ) : error ? (
            <div className="empty">
              <h4>Could not load the items</h4>
              <p>{error}</p>
              <button type="button" className="btn btn-ghost btn-sm" onClick={props.onRetry}>Retry</button>
            </div>
          ) : (
            <div className="tbl-wrap">
              <table className="tbl ops-items">
                <thead>
                  <tr>
                    {columns.map((c) => {
                      const isActive = c.sort === active.sort;
                      return (
                        <th
                          key={c.label}
                          className={c.className}
                          title={c.tip}
                          aria-sort={isActive ? (active.desc ? "descending" : "ascending") : "none"}
                        >
                          <button type="button" className={`ops-sort${isActive ? " on" : ""}`} onClick={() => c.sort && onViewChange(nextSort(view, c.sort))}>
                            {c.label}
                            <span className="material-symbols-rounded" aria-hidden="true">
                              {isActive && !active.desc ? "arrow_upward" : "arrow_downward"}
                            </span>
                          </button>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={cols}>
                        {filtered ? (
                          <div className="empty">
                            <h4>No items match these filters</h4>
                            <p>Remove a filter, or <button type="button" className="btn btn-text btn-sm" onClick={clearView}>clear all filters</button>.</p>
                          </div>
                        ) : (
                          <div className="empty"><h4>Nothing here</h4><p>No open items in this bucket.</p></div>
                        )}
                      </td>
                    </tr>
                  )}
                  {rows.map((row) => row.kind === "intake" ? (
                    <tr key={`intake-${row.item.source}-${row.item.id}`} className="linkrow desk-intake" onClick={() => window.open(intakeHref(row.item), "_blank", "noopener")}>
                      <td className="worker">
                        <a className="row-link" href={intakeHref(row.item)} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} aria-label={row.item.preOnboardingRequestId ? `Open pre-verification ${row.item.preOnboardingRequestId.slice(0, 8)} in a new tab` : `Open intake ${row.item.id.slice(0, 8)} in a new tab`}>
                          <b>{row.item.worker ?? "—"}</b>
                          <span className="material-symbols-rounded" aria-hidden="true">open_in_new</span>
                        </a>
                      </td>
                      <td className="client">{row.item.client ?? <span className="muted">—</span>}</td>
                      <td className="type">{workTypeLabel(row.item.workType)} <span className={`ops-pill src-${row.item.source}`} title="An intake request: the journey has no FBO request yet.">{INTAKE_SOURCE_LABEL[row.item.source]} intake</span></td>
                      <td className="fit">
                        <span className="ops-pill stage" title={row.item.journeyState === "awaitingRequest" ? "The work order exists; nobody has raised the request yet." : "Nothing exists in FBO for this intake yet."}>
                          {row.item.journeyState === "awaitingRequest" ? "WO created, awaiting request" : "At intake"}
                        </span>
                      </td>
                      <td className="numeric">{row.item.waitingBd}</td>
                      <td className="fit nowrap">{fmtETDate(row.item.startedUtc)}</td>
                      <td className={`numeric${row.item.sinceStatusBd > blockedAfterBd ? " warm" : ""}`}>{row.item.sinceStatusBd}</td>
                      <StartDateCell risk={row.item.startRisk} leadBd={row.item.startLeadBd} startDate={row.item.startDate} />
                      <td className="fit">
                        <span className="ops-pills">
                          <span className={`ops-pill att-${ATTENTION_CLASS[row.item.attention]}`}><i className="dot" />{ATTENTION_LABEL[row.item.attention]}</span>
                        </span>
                      </td>
                      <td>
                        <div className="ops-pending">
                          {row.item.journeyState === "awaitingRequest"
                            ? <span className="ops-pill role-workorder" title="The work order is waiting for its request on our side.">Work order · awaiting request</span>
                            : <span className="ops-pill role-none" title="Not in FBO yet: the intake itself is the pending record.">Intake · not in FBO yet</span>}
                        </div>
                      </td>
                      {!hideOwner && <td className="nowrap">{row.item.pendingWith ?? "Unassigned"}</td>}
                    </tr>
                  ) : (
                    <RequestRow key={row.item.request.id} i={row.item} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {intakes?.error && (
            <div className="ops-drawer-status" role="status">
              The intakes this owner holds could not be loaded ({intakes.error}).
              <button type="button" className="btn btn-text btn-sm" onClick={intakes.onRetry}>Retry</button>
            </div>
          )}
        </div>
        <div className="ops-drawer-foot">
          {totalCount > cap
            ? `Showing the first ${cap} of ${totalCount} matching${totalCount < bucketCount ? ` (of ${bucketCount} items)` : " items"}, ${sortSummary(view)}.`
            : `${countLabel(totalCount + heldShown, bucketCount + heldTotal)}${heldShown > 0 ? ` (${heldShown} of them ${heldShown === 1 ? "an intake" : "intakes"})` : ""}, ${sortSummary(view)}.`}
          {overflowsX && <span className="ops-drawer-scrollhint"> Scroll sideways for more columns.</span>}
        </div>
      </aside>
    </>
  );
}
